"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import Head from 'next/head';
import { Orbitron } from 'next/font/google';



const orbitron = Orbitron({
    subsets: ['latin'],
    weight: ['400', '700'], // You can pick which weights you want
});

export default function Home() {
    const [ticker, setTicker] = useState("");
    const router = useRouter();
    const handleInteractWithFilings = () => {
        // This will be the Pinecone-talk page we create next
        router.push("/Pinecone-Talk");
    };
    // ✅ Capitalize input
    const handleTickerChange = (e) => {
        setTicker(e.target.value.toUpperCase());
    };

    // ✅ Redirect user to analyze page

    const startAnalysis = () => {
        if (!ticker.trim()) {
            alert("Please enter a ticker symbol.");
            return;
        }
        router.push(`/analyze?ticker=${ticker}&showInstructions=1`);
    };

    // ✅ Listen for "Enter" key
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            startAnalysis();
        }
    };


    return (
        <div className="min-h-screen">
            <Head>
                <title>iAgentX - AI Financial Analyst</title>
            </Head>

            {/* 🔁 Fullscreen Background Video */}
            <div className="absolute top-4 left-4 z-20 flex flex-col items-start space-y-1">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-gray-300">
                        see the future
                    </span>
                <button
                    onClick={handleInteractWithFilings}
                    className={`px-8 py-3 text-lg font-bold rounded-xl border ${orbitron.className}`}
                    style={{
                        borderColor: 'rgba(255,255,255,0.4)',
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(6px)',
                        color: '#ffffff',
                        letterSpacing: '0.15em',
                        textShadow: '0 0 10px rgba(255,255,255,0.5)',
                    }}
                >
                    Ask the Analyst
                </button>
            </div>
            <div className="absolute top-35 left-4 z-20 flex flex-col items-start space-y-1">
                <button
                    onClick={() => router.push("/sample-reports029")}
                    className={`px-8 py-3 text-lg font-bold rounded-xl border ${orbitron.className}`}
                    style={{
                        borderColor: 'rgba(255,255,255,0.4)',
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(6px)',
                        color: '#ffffff',
                        letterSpacing: '0.15em',
                        textShadow: '0 0 10px rgba(255,255,255,0.5)',
                    }}
                >
                    View Research Samples
                </button>
                <div className="absolute top-[150px] right-4 z-20">
                    <button
                        onClick={() => router.push("/sample-analyst-interactions")}
                        className={`px-8 py-3 text-lg font-bold rounded-xl border ${orbitron.className}`}
                        style={{
                            borderColor: 'rgba(255,255,255,0.4)',
                            background: 'rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(6px)',
                            color: '#ffffff',
                            letterSpacing: '0.15em',
                            textShadow: '0 0 10px rgba(255,255,255,0.5)',
                        }}
                    >
                        Sample Analyst Interaction
                    </button>
                </div>
            </div>
            <div className="absolute top-4 right-4 z-20">
                <button
                    onClick={() => router.push("/about")}
                    className={`px-8 py-3 text-lg font-bold rounded-xl border ${orbitron.className}`}
                    style={{
                        borderColor: 'rgba(255,255,255,0.4)',
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(6px)',
                        color: '#ffffff',
                        letterSpacing: '0.15em',
                        textShadow: '0 0 10px rgba(255,255,255,0.5)',
                    }}
                >
                    About
                </button>
            </div>
            <div className="fixed top-0 left-0 w-full h-full -z-10 overflow-hidden bg-black">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover opacity-60 brightness-150 invert"
                >
                    <source src="/0001-0120.mp4" type="video/mp4"/>
                    Your browser does not support the video tag.
                </video>
            </div>

            {/* 🌐 Page Content */}
            <div className="relative z-10 min-h-[200vh] flex flex-col items-center justify-start">
                {/* Futuristic Header */}
                <div className="text-center max-w-4xl mx-auto mt-20 mb-16 px-4">
                    <h1 className={`text-7xl font-bold mb-6 ${orbitron.className}`}
                        style={{
                            background: 'linear-gradient(135deg, #e0e0e0 0%, #ffffff 25%, #c0c0c0 50%, #ffffff 75%, #a8a8a8 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textShadow: '0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(255,255,255,0.3)',
                            letterSpacing: '0.1em'
                        }}>
                        IAGENTX
                    </h1>
                    <h2 className={`text-2xl font-semibold tracking-wider ${orbitron.className}`}
                        style={{
                            background: 'linear-gradient(135deg, #c0c0c0 0%, #ffffff 50%, #a0a0a0 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textShadow: '0 0 20px rgba(255,255,255,0.4)'
                        }}>
                        Institutional-style equity research, generated from audited filings and open financial data
                    </h2>
                </div>

                {/* Ticker Input */}
                <div className="w-full flex flex-col items-center justify-start pt-6 pb-20">
                    <h1 className={`text-3xl font-bold mb-8 ${orbitron.className}`}
                        style={{
                            background: 'linear-gradient(135deg, #e0e0e0 0%, #ffffff 25%, #c0c0c0 50%, #ffffff 75%, #a8a8a8 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textShadow: '0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(255,255,255,0.3)',
                            letterSpacing: '0.1em'
                        }}>
                        AGENTIC FINANCIAL ANALYST USING AUDITED DATA
                    </h1>
                    <div className="flex">
                        <input
                            type="text"
                            value={ticker}
                            onChange={handleTickerChange}
                            onKeyDown={handleKeyPress}
                            placeholder="ENTER TICKER"
                            className={`px-6 py-3 border rounded-l-md text-xl font-bold ${orbitron.className}`}
                            style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: '#e0e0e0',
                                borderColor: '#666',
                                letterSpacing: '0.1em',
                                textShadow: '0 0 10px rgba(255,255,255,0.3)'
                            }}
                        />
                        <button
                            onClick={startAnalysis}
                            className={`px-8 py-3 rounded-r-md text-xl font-bold transition ${orbitron.className}`}
                            style={{
                                background: 'linear-gradient(135deg, #e0e0e0 0%, #ffffff 25%, #c0c0c0 50%, #ffffff 75%, #a8a8a8 100%)',
                                color: '#000',
                                letterSpacing: '0.1em',
                                textShadow: '0 0 20px rgba(255,255,255,0.5)',
                                border: '2px solid rgba(255,255,255,0.3)'
                            }}
                        >
                            🔍 Start Analyst Review
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}



